import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { AnimatePresence, motion } from "framer-motion";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import { copyTextToClipboard } from "../utils/clipboard";
import {
  formatRewardsEarnedLabel,
  getReferralProgressState,
  buildReferralLink,
} from "../utils/referralProgress";
import { useSelector } from "react-redux";
import { investorLeads } from "../services/investorLeads";
import logsService from "../services/logServices";
import InvestorActivityEventType from "../utils/eventTypes";

const BRAND_CORAL = "#FF382D";
const BRAND_CORAL_DARK = "#d12f26";
const WHATSAPP_GREEN = "#25D366";
const WHATSAPP_GREEN_DARK = "#1da851";
const MOBILE_BOTTOM_NAV_HEIGHT = "var(--mobile-bottom-tab-height, 84px)";
const FAB_BOUNCE_REPEAT_DELAY_S = 45;
const FAB_BOUNCE_INITIAL_DELAY_S = 1.2;
const EXPAND_SPRING = { type: "spring", stiffness: 420, damping: 26, mass: 0.85 };
const PROGRESS_TRANSITION = { duration: 1, ease: [0.22, 1, 0.36, 1] };

const DEFAULT_PROPS = {
  progress: 3,
  goal: 5,
  rewardText: "25 sqft of land",
  campaignName: "Adani Solar",
  referralLink: "https://realx.in/ref/XYZ123",
};

const tactileButtonSx = {
  transition:
    "transform 0.12s ease, background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
  "&:active": {
    transform: "scale(0.97)",
  },
};

function buildAssetLink(listingId) {
  if (!listingId) return "";
  return `/dashboard/property/${listingId}`;
}

function buildReferralShareMessage({ campaignName, rewardText, referralLink, shareMessage }) {
  if (shareMessage?.trim()) {
    return shareMessage.trim();
  }

  return [
    `Hi! I'm investing in ${campaignName} on RealX — India's fractional real estate marketplace.`,
    "",
    `Join using my referral link and explore curated property investments:`,
    referralLink,
    "",
    `Sign up through this link to get started. Rewards up to ${rewardText} for eligible referrals.`,
  ].join("\n");
}

function ReferralFab({ rewardText, onOpen }) {
  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <motion.span
        aria-hidden="true"
        animate={{
          scale: [1, 1.45, 1],
          opacity: [0.55, 0, 0.55],
        }}
        transition={{
          duration: 2.4,
          repeat: Infinity,
          ease: "easeOut",
        }}
        style={{
          position: "absolute",
          inset: -10,
          borderRadius: 999,
          background: "rgba(255, 56, 45, 0.22)",
          pointerEvents: "none",
        }}
      />
      <motion.span
        aria-hidden="true"
        animate={{
          scale: [1, 1.28, 1],
          opacity: [0.85, 0.15, 0.85],
        }}
        transition={{
          duration: 2.4,
          repeat: Infinity,
          ease: "easeOut",
          delay: 0.2,
        }}
        style={{
          position: "absolute",
          inset: -4,
          borderRadius: 999,
          border: "2px solid rgba(255, 56, 45, 0.55)",
          pointerEvents: "none",
        }}
      />
      <motion.button
        type="button"
        onClick={onOpen}
        animate={{
          scale: [1, 1.14, 0.96, 1.08, 1],
          y: [0, -12, 3, -6, 0],
          rotate: [0, -2, 1.5, -1, 0],
          boxShadow: [
            "0 10px 28px rgba(255, 56, 45, 0.38)",
            "0 22px 48px rgba(255, 56, 45, 0.62)",
            "0 8px 18px rgba(255, 56, 45, 0.28)",
            "0 16px 36px rgba(255, 56, 45, 0.5)",
            "0 10px 28px rgba(255, 56, 45, 0.38)",
          ],
        }}
        transition={{
          duration: 1.2,
          ease: "easeInOut",
          times: [0, 0.26, 0.48, 0.72, 1],
          delay: FAB_BOUNCE_INITIAL_DELAY_S,
          repeat: Infinity,
          repeatDelay: FAB_BOUNCE_REPEAT_DELAY_S,
        }}
        whileHover={{
          scale: 1.06,
          y: -2,
          backgroundColor: BRAND_CORAL_DARK,
        }}
        whileTap={{ scale: 0.94, y: 0 }}
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 20px",
          border: "none",
          borderRadius: 999,
          backgroundColor: BRAND_CORAL,
          color: "#fff",
          cursor: "pointer",
          fontSize: "0.9375rem",
          fontWeight: 600,
          lineHeight: 1.4,
          fontFamily: "inherit",
          whiteSpace: "nowrap",
          transformOrigin: "bottom right",
        }}
      >
        <motion.span
          aria-hidden="true"
          animate={{ rotate: [0, -12, 12, -8, 0], scale: [1, 1.15, 1.1, 1.12, 1] }}
          transition={{
            duration: 1.2,
            ease: "easeInOut",
            delay: FAB_BOUNCE_INITIAL_DELAY_S,
            repeat: Infinity,
            repeatDelay: FAB_BOUNCE_REPEAT_DELAY_S,
          }}
          style={{ fontSize: "1.125rem", lineHeight: 1, display: "inline-block" }}
        >
          🎁
        </motion.span>
        <span>Earn {rewardText}</span>
      </motion.button>
    </Box>
  );
}

function ReferralHeader({ campaignName, rewardText, onClose, isCouponDataPresent }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1,
        pb: 1.5,
        borderBottom: "1px solid",
        borderColor: "grey.200",
      }}
    >
      <Box sx={{ pr: 0.5 }}>
        <Typography
          sx={{
            fontSize: { xs: "1.125rem", sm: "1.25rem" },
            fontWeight: 700,
            color: "text.primary",
            lineHeight: 1.25,
            letterSpacing: "-0.01em",
          }}
        >
        {isCouponDataPresent ? "Refer and Earn 25 sqft of land" : "Get assured reward of 25 sqft of land"}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: BRAND_CORAL,
            mt: 0.35,
            fontWeight: 600,
            fontSize: "0.8125rem",
          }}
        >
          {isCouponDataPresent ? "" : "On Your 1st Investment in"}
          <Box component="span" sx={{ display: "block" }}>
            {campaignName || "Adani Solar"}
          </Box>
        </Typography>
      </Box>
      <IconButton
        size="small"
        aria-label="Close referral widget"
        onClick={onClose}
        sx={{
          color: "grey.500",
          mt: -0.25,
          mr: -0.75,
          transition: "background-color 0.15s ease, transform 0.12s ease",
          "&:hover": {
            bgcolor: "grey.100",
          },
          "&:active": {
            transform: "scale(0.92)",
          },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

function ReferralProgress({ progress, goalStep, animateKey }) {
  const {
    progress: investedCount,
    displayGoal,
    progressPercent,
    remaining,
    rewardsEarned,
  } = getReferralProgressState(progress, goalStep);
  const [animatedWidth, setAnimatedWidth] = useState(0);

  useEffect(() => {
    setAnimatedWidth(0);
    const frame = window.requestAnimationFrame(() => {
      setAnimatedWidth(progressPercent);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [progressPercent, animateKey]);

  return (
    <Box sx={{ py: 1.5, borderBottom: "1px solid", borderColor: "grey.200" }}>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, color: "text.primary", mb: 0.25, fontSize: "0.8125rem" }}
      >
        {investedCount} / {displayGoal} Invested
      </Typography>
      <Typography
        variant="caption"
        sx={{ display: "block", mb: 0.75, color: BRAND_CORAL, fontWeight: 600 }}
      >
        {formatRewardsEarnedLabel(rewardsEarned)}
      </Typography>
      <Box
        sx={{
          height: 7,
          borderRadius: 999,
          bgcolor: "grey.200",
          overflow: "hidden",
        }}
      >
        <motion.div
          initial={false}
          animate={{ width: `${animatedWidth}%` }}
          transition={PROGRESS_TRANSITION}
          style={{
            height: "100%",
            borderRadius: 999,
            backgroundColor: BRAND_CORAL,
          }}
        />
      </Box>
      <Typography
        variant="caption"
        sx={{ display: "block", mt: 0.75, color: "text.secondary", lineHeight: 1.35 }}
      >
        You&apos;re {remaining} referrals away from your next reward!
      </Typography>
    </Box>
  );
}

function ReferralNoCouponState({ rewardText, goalStep, assetLink, onNavigateToAsset }) {
  const hasAssetLink = Boolean(assetLink?.trim());

  return (
    <Box sx={{ py: 2 }}>
      <Typography
        sx={{
          fontSize: "0.9375rem",
          fontWeight: 700,
          color: "text.primary",
          lineHeight: 1.35,
          mb: 1,
        }}
      >
        Make your first investment to unlock referrals
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          fontSize: "0.8125rem",
          lineHeight: 1.5,
          mb: 1.5,
        }}
      >
        Once you invest, you&apos;ll get your personal referral link. Share it with others —
        for every {goalStep} investors who invest through your link, you get{" "}
        <Box component="span" sx={{ color: BRAND_CORAL, fontWeight: 700 }}>
          {rewardText}
        </Box>{" "}
        for free.
      </Typography>
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          mb: 1.5,
          borderRadius: 1.5,
          bgcolor: "rgba(255, 56, 45, 0.06)",
          border: "1px solid",
          borderColor: "rgba(255, 56, 45, 0.18)",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: BRAND_CORAL,
            fontWeight: 600,
            fontSize: "0.75rem",
            lineHeight: 1.4,
          }}
        >
          {goalStep} referrals = {rewardText} free
        </Typography>
      </Box>
      <Button
        fullWidth
        variant="contained"
        component={hasAssetLink ? RouterLink : "button"}
        to={hasAssetLink ? assetLink : undefined}
        disabled={!hasAssetLink}
        onClick={hasAssetLink ? onNavigateToAsset : undefined}
        sx={{
          py: 0.9,
          minHeight: 40,
          bgcolor: BRAND_CORAL,
          fontWeight: 600,
          fontSize: "0.8125rem",
          textTransform: "none",
          borderRadius: 1.5,
          boxShadow: "none",
          ...tactileButtonSx,
          "&:hover": {
            bgcolor: BRAND_CORAL_DARK,
            boxShadow: "none",
          },
        }}
      >
        Go to Asset
      </Button>
    </Box>
  );
}

function SectionLabel({ children }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: "block",
        mb: 1,
        fontWeight: 700,
        color: "grey.600",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        fontSize: "0.6875rem",
      }}
    >
      {children}
    </Typography>
  );
}

function QuickShareActions({ campaignName, rewardText, referralLink, shareMessage }) {
  const [copied, setCopied] = useState(false);
  const user = useSelector(state => state.user?.data);
  const shareText = buildReferralShareMessage({
    campaignName,
    rewardText,
    referralLink,
    shareMessage,
  });
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;

  const handleCopyLink = useCallback(async () => {
    if (!referralLink?.trim()) return;

    try {
      await copyTextToClipboard(referralLink);
      setCopied(true);
      logsService.addLog({
        userId: user?._id,
        event: InvestorActivityEventType.SHARE_LINK_COPIED
      })
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [referralLink, user?._id]);

  const actionButtonSx = {
    flex: "1 1 0",
    minWidth: 0,
    width: 0,
    py: 0.85,
    minHeight: 40,
    fontSize: "0.8125rem",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: 1.5,
    whiteSpace: "nowrap",
    ...tactileButtonSx,
  };

  return (
    <Box sx={{ py: 1.5 }}>
      <SectionLabel>Quick Share</SectionLabel>
      <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
        <Button
          variant="outlined"
          onClick={handleCopyLink}
          disabled={!referralLink?.trim()}
          sx={{
            ...actionButtonSx,
            borderColor: "grey.300",
            color: "text.primary",
            "&:hover": {
              borderColor: "grey.400",
              bgcolor: "grey.50",
            },
          }}
        >
          {copied ? "Copied!" : "Copy Link"}
        </Button>
        <Button
          component="a"
          href={referralLink?.trim() ? whatsappUrl : undefined}
          target="_blank"
          rel="noopener noreferrer"
          disabled={!referralLink?.trim()}
          onClick={()=>{
            logsService.addLog({
              userId: user?._id,
              event: InvestorActivityEventType.WHATSAPP_INVITE_SHARED
            })
          }}
          sx={{
            ...actionButtonSx,
            bgcolor: WHATSAPP_GREEN,
            color: "#fff",
            boxShadow: "none",
            "&:hover": {
              bgcolor: WHATSAPP_GREEN_DARK,
              boxShadow: "none",
            },
          }}
        >
          WhatsApp
        </Button>
      </Box>
    </Box>
  );
}

function hasMultipleEntriesWithoutComma(value = "") {
  const trimmed = String(value).trim();
  if (!trimmed || trimmed.includes(",")) return false;

  const parts = trimmed.split(/\s+/).filter(Boolean);
  return parts.length > 1;
}

function parseEmailsInput(value = "") {
  return String(value)
    .split(",")
    .map((email) => email.trim())
    .filter(Boolean);
}

const emailInvitesSchema = Yup.object().shape({
  emails: Yup.string()
    .trim()
    .required("At least one email is required")
    .test("valid-emails", function validateEmails(value) {
      const trimmed = String(value ?? "").trim();

      if (!trimmed) {
        return this.createError({ message: "At least one email is required" });
      }

      if (hasMultipleEntriesWithoutComma(trimmed)) {
        return this.createError({
          message: "Comma is required between multiple emails (e.g. user@gmail.com, friend@gmail.com)",
        });
      }

      const emails = parseEmailsInput(trimmed);

      if (emails.length === 0) {
        return this.createError({ message: "At least one email is required" });
      }

      const invalidEmails = emails.filter(
        (email) => !Yup.string().email().isValidSync(email)
      );

      if (invalidEmails.length > 0) {
        return this.createError({
          message:
            invalidEmails.length === 1
              ? `Invalid email: ${invalidEmails[0]}`
              : `Invalid emails: ${invalidEmails.join(", ")}`,
        });
      }

      return true;
    }),
});

function EmailInvitesSection({ onSendInvites, referralLink }) {
  const { enqueueSnackbar } = useSnackbar();
  const user = useSelector(state => state.user?.data);
  const formik = useFormik({
    initialValues: { emails: "" },
    validationSchema: emailInvitesSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      const parsedEmails = parseEmailsInput(values.emails);

      try {
        const res = await onSendInvites?.(parsedEmails);

        if (res?.success) {
          enqueueSnackbar(res?.message || "Invites sent successfully", { variant: "success" });
          logsService.addLog({
            userId: user?._id,
            event: InvestorActivityEventType.EMAIL_INVITE_SHARED
          });
          resetForm();
        } else {
          enqueueSnackbar(res?.message || "Failed to send invites", { variant: "error" });
        }
      } catch {
        enqueueSnackbar("Failed to send invites", { variant: "error" });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting } =
    formik;
  const canSendInvites = Boolean(referralLink?.trim());

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      sx={{
        pt: 1.5,
        pb: 1,
        borderTop: "1px solid",
        borderColor: "grey.200",
      }}
    >
      <SectionLabel>Email Invites</SectionLabel>
      <TextField
        multiline
        rows={2}
        fullWidth
        name="emails"
        placeholder="Enter email or comma-separated emails e.g. user@gmail.com, friend@gmail.com"
        value={values.emails}
        onChange={handleChange}
        onBlur={handleBlur}
        error={Boolean(touched.emails && errors.emails)}
        helperText={
          touched.emails && errors.emails
            ? errors.emails
            : canSendInvites
              ? "Add one email or multiple emails separated by commas"
              : "Referral link is loading. Please wait before sending invites."
        }
        sx={{
          mb: 1,
          "& .MuiOutlinedInput-root": {
            fontSize: "0.8125rem",
            bgcolor: "grey.50",
            borderRadius: 1.5,
            py: 0.25,
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "grey.300",
          },
        }}
      />
      <Button
        fullWidth
        type="submit"
        variant="contained"
        disabled={isSubmitting || !canSendInvites}
        sx={{
          py: 0.9,
          minHeight: 40,
          bgcolor: BRAND_CORAL,
          fontWeight: 600,
          fontSize: "0.8125rem",
          textTransform: "none",
          borderRadius: 1.5,
          boxShadow: "none",
          ...tactileButtonSx,
          "&:hover": {
            bgcolor: BRAND_CORAL_DARK,
            boxShadow: "none",
          },
        }}
      >
        Send Invites
      </Button>
    </Box>
  );
}

function ReferralFooter({ onViewTracking }) {
  const user = useSelector(state => state.user?.data);
  return (
    <Box
      sx={{
        pt: 1,
        textAlign: "center",
        borderTop: "1px solid",
        borderColor: "grey.200",
      }}
    >
      <Button
        variant="text"
        onClick={()=>{
          logsService.addLog({
            userId: user?._id,
            event: InvestorActivityEventType.INVITE_VIEW_DETAILS_CLICKED
          })
          onViewTracking();
        }}
        sx={{
          color: BRAND_CORAL,
          fontWeight: 600,
          textTransform: "none",
          fontSize: "0.8125rem",
          py: 0.35,
          minHeight: "auto",
          ...tactileButtonSx,
          "&:hover": {
            bgcolor: "rgba(255, 56, 45, 0.06)",
          },
        }}
      >
        View Detailed Tracking ➔
      </Button>
    </Box>
  );
}

function ReferralCard({
  campaignName,
  rewardText,
  progress,
  goalStep,
  referralLink,
  assetLink,
  shareMessage,
  animateKey,
  isCouponDataPresent,
  onClose,
  onSendInvites,
  onViewTracking,
  onNavigateToAsset,
}) {
  return (
    <Box
      sx={{
        width: { xs: "calc(100vw - 32px)", sm: 350 },
        maxWidth: 350,
        overflow: "hidden",
        bgcolor: "#fff",
        borderRadius: 3,
        boxShadow: "0 20px 50px rgba(33, 43, 54, 0.16), 0 4px 12px rgba(33, 43, 54, 0.08)",
        border: "1px solid",
        borderColor: "grey.200",
        p: { xs: 2, sm: 2.25 },
      }}
    >
      <ReferralHeader
        campaignName={campaignName || "Referral Rewards"}
        rewardText={rewardText}
        onClose={onClose}
        isCouponDataPresent={isCouponDataPresent}
      />
      {isCouponDataPresent ? (
        <>
          <ReferralProgress progress={progress} goalStep={goalStep} animateKey={animateKey} />
          <QuickShareActions
            campaignName={campaignName}
            rewardText={rewardText}
            referralLink={referralLink}
            shareMessage={shareMessage}
          />
          <EmailInvitesSection
            onSendInvites={onSendInvites}
            referralLink={referralLink}
          />
          <ReferralFooter onViewTracking={onViewTracking} />
        </>
      ) : (
        <ReferralNoCouponState
          rewardText={rewardText}
          goalStep={goalStep}
          assetLink={assetLink}
          onNavigateToAsset={onNavigateToAsset}
        />
      )}
    </Box>
  );
}

export default function ReferralWidget({
  rewardText = DEFAULT_PROPS.rewardText,
  shareMessage,
  onViewTracking,
}) {
  const user = useSelector(state => state?.user?.data)
  const [isOpen, setIsOpen] = useState(false);
  const [progressAnimateKey, setProgressAnimateKey] = useState(0);
  const [progress,setProgress] = useState(0);
  const [goalStep,setGoalStep] = useState(5);
  const [referralLink,setReferralLink] = useState("");
  const [assetLink,setAssetLink] = useState("");
  const [listingId,setListingId] = useState("");
  const [campaignName,setCampaignName] = useState("");
  const [isCouponDataPresent,setIsCouponDataPresent] = useState(false);
  const location = useLocation();

  const handleSendEmailInvites = useCallback(async (emails) => {
    if (!user?._id) {
      return { success: false, message: "Please log in to send invites" };
    }

    if (!referralLink?.trim()) {
      return { success: false, message: "Referral link is not available yet" };
    }

    return investorLeads.sendEmailInvites({
      userId: user._id,
      emails,
      referralLink,
      listingId,
    });
  }, [user?._id, referralLink, listingId]);

const getInvestedInvestorLeadsData = async () => {
  try {
    const res = await investorLeads.getInvested(user?._id);
    if (res && res.success) {
      const hasCouponData = Boolean(res?.couponData);
      setProgress(res.investedLeads || 0);
      setGoalStep(res.requiredReferrals || 5);
      setReferralLink(buildReferralLink(res?.couponData));
      setListingId(res?.listing?._id);
      setAssetLink(buildAssetLink(res?.listing?._id));
      setIsCouponDataPresent(hasCouponData);
      setCampaignName(res?.listing?.title);
    }
  } catch (error) {
    console.log(error);
  }
}
useEffect(() => {
  if (user?._id) {
    getInvestedInvestorLeadsData();
  }
}, [user?._id]);

  const handleViewTracking = useCallback(() => {
    setIsOpen(false);
    onViewTracking?.();
  }, [onViewTracking]);

  const handleNavigateToAsset = useCallback(() => {
    setIsOpen(false);
    logsService.addLog({
      userId: user?._id,
      event: InvestorActivityEventType.GO_TO_ASSET_CLICKED
    })
  }, [user?._id]);

  useEffect(() => {
    if (location.pathname === "/referrals") {
      setIsOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isOpen) {
      setProgressAnimateKey((key) => key + 1);
    }
  }, [isOpen, progress]);

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: {
          xs: `calc(${MOBILE_BOTTOM_NAV_HEIGHT} + 16px + env(safe-area-inset-bottom, 0px))`,
          md: "24px",
        },
        right: { xs: "16px", sm: "24px" },
        left: { xs: "auto", sm: "auto" },
        zIndex: 1400,
        transformOrigin: "bottom right",
      }}
    >
      <AnimatePresence mode="wait">
        {isOpen ? (
          <motion.div
            key="referral-card"
            initial={{ opacity: 0, scale: 0.68, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.68, y: 24 }}
            transition={EXPAND_SPRING}
            style={{ transformOrigin: "bottom right" }}
          >
            <ReferralCard
              campaignName={campaignName}
              rewardText={rewardText}
              progress={progress}
              goalStep={goalStep}
              referralLink={referralLink}
              assetLink={assetLink}
              shareMessage={shareMessage}
              animateKey={progressAnimateKey}
              isCouponDataPresent={isCouponDataPresent}
              onClose={() => setIsOpen(false)}
              onSendInvites={handleSendEmailInvites}
              onViewTracking={handleViewTracking}
              onNavigateToAsset={handleNavigateToAsset}
            />
          </motion.div>
        ) : (
          <motion.div
            key="referral-fab"
            initial={{ opacity: 0, scale: 0.68, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.68, y: 24 }}
            transition={EXPAND_SPRING}
            style={{ transformOrigin: "bottom right" }}
          >
            <ReferralFab
              rewardText={rewardText}
              onOpen={() => setIsOpen(true)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}
